import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import '../styles/Pricing.css';

const HOME_URL = 'https://icraftz.com';

const TIERS = [
  {
    id: 'arambh',
    name: 'Arambh',
    devanagari: 'आरंभ',
    meaning: 'The Beginning',
    accent: 'green',
    price: 'Free',
    priceSuffix: 'forever — on the house',
    launchNote: 'Available from Day 1',
    description:
      'Step into the library. A handpicked starter shelf to feel what iCraftz can do for your thinking.',
    features: [
      '21 books — handpicked across 9 life categories',
      '10 Indian languages',
      'Summary, Key Insights, Action Items & Core Theme',
      'Basic library browsing',
    ],
    cta: { label: 'Create free account', to: '/signup', primary: false },
  },
  {
    id: 'jigyasu',
    name: 'Jigyasu',
    devanagari: 'जिज्ञासु',
    meaning: 'The Curious Seeker',
    accent: 'teal',
    featured: true,
    price: '₹199',
    priceSuffix: 'per month',
    launchNote: 'Launches Month 6',
    description:
      'For the reader who wants to go deeper. Wider shelf, your own reflections, podcasts, and the community of fellow seekers.',
    features: [
      '52 books access',
      '10 Indian languages',
      'Everything in Arambh',
      'Book Whispers — Your Turn',
      'Chapters Within — Your Story',
      'Curated Collections + Infographics + Podcasts',
    ],
    cta: { label: 'Notify me at launch', to: '/#waitlist', primary: true },
  },
  {
    id: 'gyaani',
    name: 'Gyaani',
    devanagari: 'ज्ञानी',
    meaning: 'The Wise One',
    accent: 'gold',
    price: '₹399',
    priceSuffix: 'per month',
    launchNote: 'Launches Month 6',
    description:
      'For the lifelong student. The fullest shelf, priority on every new book, and a private WhatsApp cohort of readers.',
    features: [
      '101 books access',
      '10 Indian languages',
      'Everything in Jigyasu',
      'Book Whispers — Your Turn',
      'Chapters Within — Your Story',
      'Priority new book additions',
      'Exclusive WhatsApp Book Reading Cohort',
    ],
    cta: { label: 'Notify me at launch', to: '/#waitlist', primary: false },
  },
];

function TierCard({ tier }) {
  return (
    <article
      className={`pricing-card pricing-card-${tier.accent}${tier.featured ? ' is-featured' : ''}`}
      data-testid={`pricing-card-${tier.id}`}
    >
      {tier.featured && (
        <span className="pricing-card-badge" aria-label="Most popular">
          <Sparkles size={11} /> Most loved
        </span>
      )}
      <header className={`pricing-card-banner pricing-banner-${tier.accent}`}>
        <h2 className="pricing-card-name">{tier.name}</h2>
        <p className="pricing-card-meaning">
          <span className="pricing-card-devanagari">{tier.devanagari}</span>
          <span className="pricing-card-meaning-sep">·</span>
          <span className="pricing-card-meaning-text">{tier.meaning}</span>
        </p>
      </header>

      <div className="pricing-card-body">
        <div className="pricing-price-wrap">
          <p className="pricing-price">{tier.price}</p>
          <p className="pricing-price-suffix">{tier.priceSuffix}</p>
          <p className="pricing-launch-note">{tier.launchNote}</p>
        </div>

        <span className="pricing-card-rule" aria-hidden="true" />

        <p className="pricing-card-desc">{tier.description}</p>

        <ul className="pricing-features">
          {tier.features.map((f) => (
            <li key={`${tier.id}-${f}`} className="pricing-feature">
              <Check size={14} className="pricing-feature-icon" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {tier.cta.to.startsWith('/#') ? (
          <a
            href={tier.cta.to}
            className={`pricing-cta${tier.cta.primary ? ' pricing-cta-primary' : ''}`}
            data-testid={`pricing-cta-${tier.id}`}
          >
            {tier.cta.label}
          </a>
        ) : (
          <Link
            to={tier.cta.to}
            className={`pricing-cta${tier.cta.primary ? ' pricing-cta-primary' : ''}`}
            data-testid={`pricing-cta-${tier.id}`}
          >
            {tier.cta.label}
          </Link>
        )}
      </div>
    </article>
  );
}

export default function PricingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Pricing — iCraftz';
  }, []);

  return (
    <div className="pricing-page" data-testid="pricing-page">
      <header className="pricing-topbar">
        <a href={HOME_URL} className="pricing-logo" data-testid="pricing-logo">
          iCraftz<sup>™</sup>
        </a>
        <a href={HOME_URL} className="pricing-topbar-back" data-testid="pricing-back-top">
          <ArrowLeft size={14} /> Back to Home
        </a>
      </header>

      <main className="pricing-main">
        <section className="pricing-hero">
          <p className="pricing-eyebrow">The iCraftz Membership Roadmap</p>
          <h1 className="pricing-title">
            Less noise. <span className="pricing-title-accent">More thought.</span>
          </h1>
          <p className="pricing-sub">
            Launching <strong>6 months from today</strong> — but you can start exploring,
            free, from Day 1. Pick the shelf that fits the reader you want to become.
          </p>
          <span className="pricing-divider" aria-hidden="true">
            <span className="pricing-divider-line" />
            <span className="pricing-divider-mark">❦</span>
            <span className="pricing-divider-line" />
          </span>
        </section>

        <section className="pricing-grid" aria-label="Membership tiers">
          {TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </section>

        <section className="pricing-notes">
          <h3 className="pricing-notes-title">A few quiet promises</h3>
          <ul className="pricing-notes-list">
            <li>
              <strong>Free is forever free.</strong> Arambh isn't a trial — it's a real
              shelf, always open, no card ever asked.
            </li>
            <li>
              <strong>Cancel anytime.</strong> Paid tiers are month-to-month. No annual
              traps, no hidden fees, no dark patterns.
            </li>
            <li>
              <strong>Indian-first.</strong> 10 Indian languages on every tier. Your
              mother tongue isn't an upgrade.
            </li>
            <li>
              <strong>Built to be applied.</strong> Every plan gives you summaries,
              insights, action items, and the core theme — because reading without
              application is just entertainment.
            </li>
          </ul>
        </section>

        <section className="pricing-cta-strip">
          <p className="pricing-cta-strip-line">
            Not ready yet? <Link to="/#waitlist">Join the waitlist</Link> and we'll
            send a single, quiet note when the doors open.
          </p>
        </section>
      </main>

      <footer className="pricing-foot">
        <p>
          <Link to="/about">About</Link>
          <span> · </span>
          <Link to="/mission">Mission</Link>
          <span> · </span>
          <Link to="/partner">Partner</Link>
          <span> · </span>
          <Link to="/for-authors">For Authors</Link>
          <span> · </span>
          <Link to="/contact">Contact</Link>
          <span> · </span>
          <Link to="/privacy">Privacy</Link>
          <span> · </span>
          <Link to="/terms">Terms</Link>
        </p>
        <p className="pricing-foot-copy">
          © {new Date().getFullYear()} iCraftz™. Less Noise · More Thought.
        </p>
      </footer>
    </div>
  );
}
